<template>
  <div>
    <helpers-section-header class="mt-6 mb-2"> 13F Filing Signal Rules </helpers-section-header>
    <p class="text-body-2 mb-6">
      13F filings are quarterly reports required of all institutional investment managers with
      over $100M in assets. They disclose long equity holdings at the end of each quarter.
      This signal buys stocks that appear in the top holdings of your selected group of managers,
      based on the criteria you configure below. The core idea: follow what sophisticated
      institutions are accumulating.
    </p>

    <!-- Fund Group Manager -->
    <helpers-section-header class="font-weight-light"> Fund Selection </helpers-section-header>
    <core-fund-group-manager
      ref="fundGroupManager"
      :filer-group="criteria.filer_group"
      backtester
      whale-trader
      flat
      no-padding
      @update:filer-group="
        newVal => {
          criteria.filer_group.id = newVal.id
          criteria.filer_group.filers = newVal.filers
        }
      "
    >
      <template #predefined-groups>
        <!-- Whalescore filers — new configurable dialog -->
        <v-btn
          small
          class="ma-1"
          :disabled="!subscription || subscription === 'STANDARD'"
          @click="openWhalescoreDialog"
        >
          Whalescore Filers
        </v-btn>

        <!-- Legacy read-only badge shown when an existing model uses a legacy whalescore value -->
        <v-chip
          v-if="criteria.whalescore && criteria.whalescore.value !== 9"
          class="ma-1"
          color="warning"
          variant="outlined"
          size="small"
          closable
          @click:close="criteria.whalescore = null"
        >
          Legacy: {{ legacyWhalescoreLabel }}
        </v-chip>

        <!-- Whalescore Filers config dialog -->
        <v-dialog v-model="whalescoreDialog" persistent max-width="480">
          <v-card>
            <v-card-title>Whalescore Filer Selection</v-card-title>
            <v-card-text>
              <v-row dense>
                <v-col cols="12">
                  <v-text-field
                    v-model.number="wsDraft.num_funds"
                    variant="outlined"
                    density="compact"
                    label="Number of funds to use (1–100)"
                    type="number"
                    min="1"
                    max="100"
                  />
                </v-col>
                <v-col cols="12">
                  <v-select
                    v-model="wsDraft.sort_by"
                    variant="outlined"
                    density="compact"
                    label="Sort fund selection by"
                    :items="wsSortOptions"
                    item-title="label"
                    item-value="value"
                  />
                </v-col>
                <v-col cols="12">
                  <v-select
                    v-model="wsDraft.backtest_method"
                    variant="outlined"
                    density="compact"
                    label="Whalescore version"
                    :items="wsMethodOptions"
                    item-title="label"
                    item-value="value"
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="wsDraft.min_aum"
                    variant="outlined"
                    density="compact"
                    label="Min AUM ($M)"
                    type="number"
                    min="0"
                    clearable
                  />
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="wsDraft.max_aum"
                    variant="outlined"
                    density="compact"
                    label="Max AUM ($M)"
                    type="number"
                    min="0"
                    clearable
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="wsDraft.min_holdings"
                    variant="outlined"
                    density="compact"
                    label="Minimum holdings in portfolio"
                    type="number"
                    min="0"
                    clearable
                  />
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn variant="text" @click="whalescoreDialog = false">Cancel</v-btn>
              <v-btn color="primary" @click="applyWhalescoreConfig">Apply</v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Choose Filers by Performance -->
        <v-btn small class="ma-1" @click="fundPerformanceDialog = true">
          Dynamic Fund Selection
        </v-btn>
        <v-dialog v-model="crowded_dialog" persistent max-width="370">
          <template #activator="{ props }">
            <v-btn class="ma-1" small v-bind="props"> Unique Trades </v-btn>
          </template>
          <v-card>
            <v-card-title> Unique Trades </v-card-title>
            <v-card-text>
              <p>
                Stocks are ranked according to 1) past 12 months price performance and 2) how widely
                held the stocks are based on 13F filings. Stocks with higher performance will be
                ranked higher. Stocks less widely held will be ranked higher.
              </p>
              <v-row density="compact">
                <v-col cols="12">
                  <v-checkbox
                    v-model="criteria.crowded_stocks"
                    density="compact"
                    label="Select Stocks Based on Unique Trade Rankings"
                    @click="CrowdedStockSelected()"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="criteria.crowded_stocks_avg_min"
                    variant="outlined"
                    density="compact"
                    label="Minimum Avg. Ranking"
                    :disabled="!criteria.crowded_stocks"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="criteria.crowded_stocks_held_min"
                    variant="outlined"
                    density="compact"
                    label="Minimum % Held Ranking"
                    :disabled="!criteria.crowded_stocks"
                  />
                </v-col>
                <v-col cols="12">
                  <v-text-field
                    v-model="criteria.crowded_stocks_perf_min"
                    variant="outlined"
                    density="compact"
                    label="Minimum 12m Perf. Ranking"
                    :disabled="!criteria.crowded_stocks"
                  />
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn small @click="crowded_dialog = false"> OK </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-btn small class="ma-1" @click="HeatMapSelected()"> Use Heat Map Stocks </v-btn>
      </template>
    </core-fund-group-manager>

    <helpers-section-header class="font-weight-light layout">
      Report options
      <v-spacer />
      <v-btn text color="error" class="ma-0" @click="resetBacktesterOptions()">
        Reset options
      </v-btn>
    </helpers-section-header>
    <BasicOptions />

    <fund-performance-dialog ref="FundPerformanceDialog" whale-trader />
  </div>
</template>

<script>
import { useUserStore, useAppStore, useWwStore } from '@/store'
// Modules
import { mapWritableState } from 'pinia'
import cloneDeep from 'lodash/cloneDeep'
import FundPerformanceDialog from '@/components/page/whale-backtester/signals/_WtFundPerformanceDialog'
import AccessMixin from '@/mixins/access'
import { mapStoreState, mapStoreGetters } from '@/store/helpers'
import BasicOptions from '@/components/page/whale-backtester/signals/_F13Basic'
import { defaultSelectedGroup } from '@/data/constants'
export default {
  components: {
    BasicOptions,
    FundPerformanceDialog
  },
  mixins: [AccessMixin],

  data() {
    return {
      crowded_dialog: false,
      and_or: 'or',
      dialog: false,
      fundPerformanceDialog: false,
      whalescoreDialog: false,
      wsDraft: {
        num_funds: 30,
        sort_by: 'quarterly',
        backtest_method: 8,
        min_aum: null,
        max_aum: null,
        min_holdings: null
      },
      wsSortOptions: [
        { label: 'Top Quarterly Whalescore', value: 'quarterly' },
        { label: 'Top 1-Year Average Whalescore', value: 'one_year' },
        { label: 'Top 3-Year Average Whalescore', value: 'three_year' }
      ],
      wsMethodOptions: [
        { label: 'Original (v1)', value: 5 },
        { label: '2.0', value: 8 },
        { label: 'Uncorrelated Alpha', value: 10 }
      ],
      // Legacy items kept for display-only labeling of old saved models
      whalescoreFilersItems: [
        { description: 'Top 30 WhaleScores each quarter (equal-wt)', value: 1 },
        { description: 'Top 30 WhaleScores each quarter (mgr-wt)', value: 2 },
        { description: 'All having a 1-yr avg WhaleScore (equal-weight) beating S&P 500', value: 3 },
        { description: 'All funds having a 1-yr avg WhaleScore (manager-weight) beating S&P 500', value: 4 },
        { description: 'All funds having a 3-yr avg WhaleScore (equal-weight) beating S&P 500', value: 5 },
        { description: 'All funds having a 3-yr avg WhaleScore (manager-weight) beating S&P 500', value: 6 },
        { description: 'Custom Testing', value: 7 },
        { description: 'Custom Testing 2', value: 458 }
      ]
    }
  },
  computed: {
    ...mapStoreState({
      quarters: state => state.ww.quarters.unshift({ id: 0, description: 'All Available Dates' }),
      groups: state => state.user.groups
    }),
    ...mapWritableState(useAppStore, ['whaleTraderCriteria', 'signalView', 'exit_time_periods']),

    // Computed criteria based on ruleType prop - avoids prop mutation warning
    criteria() {
      return this.whaleTraderCriteria.signals.f13_signal
    },
    appcriteria() {
      return this.whaleTraderCriteria
    },
    enabled() {
      return this.whaleTraderCriteria.signals.f13_signal.enabled
    },
    /* ...mapFields({
      app_criteria: 'app.whaleTraderCriteria',
      criteria: 'app.whaleTraderCriteria.signals.f13_signal',
      enabled: 'app.whaleTraderCriteria.signals.f13_signal.enabled',
      signalView: 'app.signalView'
    }),*/
    ...mapStoreGetters({
      subscription: 'user/subscription'
    }),
    legacyWhalescoreLabel() {
      if (!this.criteria.whalescore || this.criteria.whalescore.value === 9) return ''
      const found = this.whalescoreFilersItems.find(x => x.value === this.criteria.whalescore.value)
      return found ? found.description : `Value ${this.criteria.whalescore.value}`
    }
  },
  methods: {
    openWhalescoreDialog() {
      // Pre-populate draft from existing config if already set to value 9
      const ws = this.criteria.whalescore
      if (ws && ws.value === 9) {
        this.wsDraft = {
          num_funds: ws.num_funds ?? 30,
          sort_by: ws.sort_by ?? 'quarterly',
          backtest_method: ws.backtest_method ?? 8,
          min_aum: ws.min_aum ?? null,
          max_aum: ws.max_aum ?? null,
          min_holdings: ws.min_holdings ?? null
        }
      } else {
        this.wsDraft = { num_funds: 30, sort_by: 'quarterly', backtest_method: 8, min_aum: null, max_aum: null, min_holdings: null }
      }
      this.whalescoreDialog = true
    },
    applyWhalescoreConfig() {
      const numFunds = Math.min(Math.max(parseInt(this.wsDraft.num_funds) || 30, 1), 100)
      this.criteria.whalescore = {
        value: 9,
        num_funds: numFunds,
        sort_by: this.wsDraft.sort_by || 'quarterly',
        backtest_method: this.wsDraft.backtest_method || 8,
        min_aum: this.wsDraft.min_aum || null,
        max_aum: this.wsDraft.max_aum || null,
        min_holdings: this.wsDraft.min_holdings || null,
        description: `Top ${numFunds} Whalescore Filers`
      }
      this.criteria.heat_map = false
      this.criteria.crowded_stocks = false
      this.criteria.filer_performance.top_performing_type = {
        description: 'Do not select filers based on performance',
        value: 0,
        requires_subscription: true
      }
      this.criteria.filer_performance.selected = false
      this.clear_group()
      this.whalescoreDialog = false
    },
    WhaleScoreFilerSelected(group) {
      this.criteria.whalescore = group
      this.criteria.heat_map = false
      this.criteria.crowded_stocks = false
      this.criteria.filer_performance.top_performing_type = {
        description: 'Do not select filers based on performance',
        value: 0,
        requires_subscription: true
      }
      this.criteria.filer_performance.selected = false
      this.clear_group()
    },
    clear_group() {
      useAppStore().setSelectedGroup(null)
      this.criteria.filer_group = cloneDeep(defaultSelectedGroup)
    },
    HeatMapSelected() {
      this.criteria.whalescore = null
      this.criteria.heat_map = true
      this.criteria.crowded_stocks = false
      this.criteria.filer_performance.top_performing_type = {
        description: 'Do not select filers based on performance',
        value: 0,
        requires_subscription: true
      }
      this.criteria.filer_performance.selected = false
      this.clear_group()
    },
    CrowdedStockSelected() {
      if (this.criteria.crowded_stocks) {
        this.criteria.whalescore = null
        this.criteria.heat_map = false
        this.criteria.filer_performance.top_performing_type = {
          description: 'Do not select filers based on performance',
          value: 0,
          requires_subscription: true
        }
        this.criteria.filer_performance.selected = false

        this.clear_group()
      }
    }
  }
}
</script>
